import { useAuth0 } from '@auth0/auth0-react';
import { faBars, faXmarkLarge } from '@fortawesome/pro-solid-svg-icons';
import {
  cn,
  CorsoLogo,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Icon,
  Menu,
  type SupportedIcon,
} from 'corso-admin-ui';
import {
  type ComponentProps,
  type ComponentType,
  type PropsWithChildren,
  type ReactNode,
  useState,
} from 'react';
import { NavLink, useMatch, type To } from 'react-router-dom';
import CommandSearch from '~/components/CommandSearch';
import { sidebarActionVariants, SidebarIcon } from '~/components/sidebar';
import { storeSections } from '~/constants/storeSections';
import { useRecentStores } from '~/hooks/useRecentStores';
import { DashboardSubLinks } from '~/pages/dashboards/DashboardSubLinks';
import { useUser } from '~/providers/AuthenticatedUserProvider';
import { useActiveStore } from '~/providers/StoreProvider';

function SidebarAction({
  name,
  onClick,
  icon,
  active = false,
  title,
}: {
  name: ReactNode;
  onClick?: () => void;
  icon: SupportedIcon;
  active?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={sidebarActionVariants({
        active: `${active}`,
        kind: 'button',
      })}
    >
      <SidebarIcon active={active} icon={icon} />
      <span className="truncate lg:group-data-[collapsed=true]/sidebar:sr-only">
        {name}
      </span>
    </button>
  );
}

function SidebarLink({
  name,
  to,
  icon,
  activeIcon,
  count,
  subdued = false,
  end = false,
  subLinks: SubLinks,
  onClick,
}: {
  name: ReactNode;
  to: string;
  icon?: SupportedIcon;
  activeIcon?: SupportedIcon;
  count?: number;
  subdued?: boolean;
  end?: boolean;
  // a section's sub-pages, offered in place of the link while one of its pages is open
  subLinks?: ComponentType<{
    children: (links: { name: string; to: To }[]) => ReactNode;
  }>;
  onClick?: () => void;
}) {
  const isExternal = /^https?/.test(to);
  const isOpen = useMatch({ path: to, end: false }) !== null;

  if (isOpen && SubLinks) {
    return (
      <>
        <div className="hidden lg:group-data-[collapsed=true]/sidebar:block">
          <DropdownMenu>
            <DropdownMenuTrigger
              className={sidebarActionVariants({
                active: `${true}`,
                kind: 'button',
              })}
            >
              {icon && (
                <SidebarIcon active icon={icon} activeIcon={activeIcon} />
              )}
              <span className="sr-only">{name}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start">
              <DropdownMenuLabel>{name}</DropdownMenuLabel>
              <SubLinks>
                {(links) =>
                  links.map((link) => (
                    <DropdownMenuItem key={link.name} asChild>
                      <NavLink to={link.to} onClick={onClick}>
                        {link.name}
                      </NavLink>
                    </DropdownMenuItem>
                  ))
                }
              </SubLinks>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="lg:group-data-[collapsed=true]/sidebar:hidden">
          <div className={sidebarActionVariants({ active: 'open' })}>
            {icon && <SidebarIcon active icon={icon} activeIcon={activeIcon} />}
            <span className="truncate">{name}</span>
          </div>
          <SubLinks>
            {(links) => (
              <ul className="mt-1 space-y-1">
                {links.map((link) => (
                  <li key={link.name}>
                    <NavLink
                      to={link.to}
                      className={({ isActive }) =>
                        sidebarActionVariants({
                          active: `${isActive}`,
                          className: 'pl-10 font-medium lg:pl-9',
                        })
                      }
                      onClick={onClick}
                    >
                      <span className="truncate">{link.name}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </SubLinks>
        </div>
      </>
    );
  }

  return (
    <NavLink
      to={to}
      end={end}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noreferrer' : undefined}
      onClick={onClick}
      title={typeof name === 'string' ? name : undefined}
      className={({ isActive }) =>
        cn(
          sidebarActionVariants({ active: `${isActive}` }),
          // LOCAL: subdued tone for items inside a subdued group (Recently Viewed)
          subdued && !isActive && 'text-neutral-500',
        )
      }
    >
      {({ isActive }) => (
        <>
          {icon && (
            <SidebarIcon
              active={isActive}
              icon={icon}
              activeIcon={activeIcon}
            />
          )}
          <span className="truncate lg:group-data-[collapsed=true]/sidebar:sr-only">
            {name}
          </span>
          {/* LOCAL: count badge — not in merchant-ui */}
          {count !== undefined && (
            <span
              className={cn(
                'ml-auto rounded-full border px-2 py-0.5 text-[10px] font-semibold lg:group-data-[collapsed=true]/sidebar:hidden',
                isActive
                  ? 'border-corso-blue-100 text-corso-blue-100'
                  : 'border-corso-blue-500 text-corso-blue-600',
              )}
            >
              {count}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

type SidebarLinkItem = Omit<
  ComponentProps<typeof SidebarLink>,
  'onClick' | 'subdued'
> & {
  hidden?: boolean;
};

type SidebarLinkGroup = {
  name: string;
  links: SidebarLinkItem[];
  hidden?: boolean;
  subdued?: boolean;
};

export function SidebarLinkGroups({
  linkGroups,
  onLinkClick,
}: {
  linkGroups: SidebarLinkGroup[];
  onLinkClick?: () => void;
}) {
  return (
    <ul className="flex flex-1 flex-col gap-y-6 lg:group-data-[collapsed=true]/sidebar:gap-2">
      {linkGroups.map((group) => (
        <li key={group.name} hidden={group.hidden}>
          {/* LOCAL: merchant-ui group header is `text-xs font-medium`; we
              switched to uppercased/tracked headers to match the mock, and
              added the lighter `subdued` variant for the Recently Viewed group. */}
          <div
            className={cn(
              'leading-normal uppercase lg:group-data-[collapsed=true]/sidebar:sr-only',
              group.subdued
                ? 'text-[11px] font-medium text-neutral-500'
                : 'text-xs font-semibold tracking-wider text-neutral-700',
            )}
          >
            {group.name}
          </div>
          <ul className="mt-1 space-y-1">
            {group.links.map((link) => (
              <li key={link.to} hidden={link.hidden}>
                <SidebarLink
                  name={link.name}
                  to={link.to}
                  icon={link.icon}
                  activeIcon={link.activeIcon}
                  count={link.count}
                  end={link.end}
                  subLinks={link.subLinks}
                  subdued={group.subdued}
                  onClick={onLinkClick}
                />
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

function AccountMenu() {
  const { logout } = useAuth0();
  const user = useUser();

  const initials =
    `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() ||
    user.email[0]?.toUpperCase();

  return (
    <Menu
      align="end"
      buttonAs={
        <button
          type="button"
          className="flex size-8 items-center justify-center rounded-full bg-neutral-800 text-xs font-semibold text-white"
          title="Account"
        >
          {initials}
        </button>
      }
    >
      <div className="flex gap-2 px-2 py-1.5">
        <div className="truncate text-sm">
          <p>
            {user.firstName} {user.lastName}
          </p>
          <p className="text-xs text-neutral-600">{user.email}</p>
        </div>
      </div>
      <hr className="my-1" />
      <Menu.ItemButton onClick={() => logout()}>Log Out</Menu.ItemButton>
    </Menu>
  );
}

function AppHeader({
  onToggleMobileSidebar,
  isMobileSidebarOpen,
}: {
  onToggleMobileSidebar: () => void;
  isMobileSidebarOpen: boolean;
}) {
  return (
    <header className="relative col-span-full">
      <div className="relative isolate grid grid-cols-[auto_1fr_auto] grid-rows-1 place-items-center gap-4 bg-neutral-900 px-4 py-2">
        <button
          type="button"
          onClick={onToggleMobileSidebar}
          className={cn(
            'flex size-8 items-center justify-center rounded-full p-1 focus-within:bg-neutral-700 hover:bg-neutral-700 lg:hidden',
            isMobileSidebarOpen && 'bg-neutral-700',
          )}
        >
          <span className="sr-only">
            {isMobileSidebarOpen ? 'Close' : 'Open'} Menu
          </span>
          <Icon
            icon={isMobileSidebarOpen ? faXmarkLarge : faBars}
            className="text-white"
          />
        </button>
        {/* LOCAL: merchant-ui renders just `<CorsoLogo />` in the left slot.
            We append a tiny "command center" pill so this app is obvious at
            a glance — same logo + a wink that you're in the back-of-house. */}
        <NavLink
          to="/"
          className="hidden items-center gap-2 text-white lg:flex"
          aria-label="Home"
        >
          <CorsoLogo />
          <span className="rounded-full border border-white/20 bg-white/10 px-2 py-0.5 font-mono text-[10px] tracking-wider text-white/80 uppercase">
            ⌘ Command Center
          </span>
        </NavLink>
        <CommandSearch />
        <nav className="flex items-center gap-2">
          <AccountMenu />
        </nav>
      </div>
    </header>
  );
}

export function CommandStandaloneLayout({ children }: PropsWithChildren) {
  // LOCAL: merchant-ui persists this via `useLocalStorage('sidebar-collapsed')`
  // from `usehooks-ts`. Plain `useState` for now until we decide whether to
  // bring in usehooks-ts.
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const closeMobileSidebar = () => setIsMobileSidebarOpen(false);

  const recentStores = useRecentStores();
  const { storeId, store } = useActiveStore();

  const activeStoreName =
    store?.name ??
    recentStores.find((s) => s.id === storeId)?.name ??
    `Store #${storeId}`;

  const activeStoreGroup =
    storeId === undefined
      ? undefined
      : {
          name: activeStoreName,
          links: storeSections.map((section) => ({
            name: section.label,
            to: `/stores/${storeId}/${section.to}`,
            icon: section.icon,
            activeIcon: section.activeIcon,
          })),
        };

  const recentlyViewedGroup =
    storeId === undefined && recentStores.length > 0
      ? {
          name: 'Recently Viewed',
          subdued: true,
          links: recentStores.map((s) => ({
            name: s.name,
            to: `/stores/${s.id}`,
          })),
        }
      : undefined;

  // LOCAL: mock data for the internal-admin nav. Merchant-ui builds its groups
  // dynamically from useEnabledClaimType / role flags; we hard-code groups
  // here until the real data wiring lands.
  const primaryLinkGroups = [
    {
      name: 'Platform',
      links: [
        {
          name: 'Stores',
          to: '/stores',
          icon: 'store',
          activeIcon: 'storeSolid',
          end: true,
        },
        {
          name: 'Shipping Claims',
          to: '/shipping-claims',
          icon: 'productGroup',
          activeIcon: 'productGroupSolid',
        },
        {
          name: 'Users',
          to: '/users',
          icon: 'users',
          activeIcon: 'usersSolid',
        },
        {
          name: 'Pricing Sets',
          to: '/pricing-sets',
          icon: 'tag',
          activeIcon: 'tagsSolid',
        },
        {
          name: 'Organizations',
          to: '/organizations',
          icon: 'organization',
          activeIcon: 'organizationSolid',
        },
        {
          name: 'Store Alerts',
          to: '/store-alerts',
          icon: 'bell',
          activeIcon: 'bellSolid',
        },
        {
          name: 'Sidekick',
          to: '/sidekick',
          icon: 'aiInline',
          activeIcon: 'ai',
        },
        {
          name: 'Dashboards',
          to: '/dashboards',
          icon: 'analytics',
          activeIcon: 'analyticsSolid',
          subLinks: DashboardSubLinks,
        },
      ],
    },
    ...(activeStoreGroup ? [activeStoreGroup] : []),
    ...(recentlyViewedGroup ? [recentlyViewedGroup] : []),
  ] satisfies SidebarLinkGroup[];

  return (
    // LOCAL: merchant-ui also reserves a third grid column for the AI chat
    // panel (`var(--corso-ai-col)`); we drop that column entirely.
    <div
      className="relative grid h-dvh grid-cols-1 grid-rows-[auto_1fr] overflow-hidden bg-neutral-900 lg:grid-cols-[var(--sidebar-col)_1fr]"
      style={{ '--sidebar-col': isCollapsed ? 'auto' : 'minmax(auto, 15rem)' }}
    >
      <AppHeader
        onToggleMobileSidebar={() => setIsMobileSidebarOpen((open) => !open)}
        isMobileSidebarOpen={isMobileSidebarOpen}
      />

      <aside
        className={cn(
          'group/sidebar hidden flex-col overflow-auto bg-neutral-200 lg:flex lg:rounded-tl-lg',
          isMobileSidebarOpen && 'flex',
        )}
        data-collapsed={isCollapsed}
      >
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-2">
          <SidebarLinkGroups
            linkGroups={primaryLinkGroups}
            onLinkClick={closeMobileSidebar}
          />
          {/* LOCAL: merchant-ui's footer has Swatches / Settings / Help links
              alongside the collapse toggle. Settings + Help are folded into
              the Platform group above, Swatches isn't relevant here, so only
              the collapse toggle remains. */}
          <ul className="mt-auto space-y-1">
            <li className="hidden pb-2 lg:list-item">
              <SidebarAction
                name={isCollapsed ? 'Expand' : 'Collapse'}
                title={isCollapsed ? 'Expand' : 'Collapse'}
                onClick={() => setIsCollapsed((c) => !c)}
                icon={isCollapsed ? 'chevronRight' : 'chevronLeft'}
              />
            </li>
          </ul>
        </nav>
      </aside>

      <div
        className={cn(
          'hidden overflow-auto overscroll-none bg-neutral-100 p-4 lg:block lg:rounded-tr-lg lg:px-12',
          !isMobileSidebarOpen && 'block',
        )}
      >
        {children}
      </div>
    </div>
  );
}
